import { getFilesFromDirectory } from './files';
import type { FormImageSource } from '../offline/formStorage';
// Desde `imageNaming` (puro), NO desde `image.ts`: ese arrastra `sketch.ts` y
// con él el cliente Apollo entero.
import { generateImageNameWithROID, type OrderMetadata } from './imageNaming';

export interface PlannedUpload {
  /** Nombre ROID definitivo. Es la clave con la que se compara contra la BD. */
  name: string;
  /** Nombre del fichero tal y como está guardado en OPFS. */
  blobFileName: string;
  /** 1-indexado; alimenta el sufijo del nombre ROID. */
  sequence: number;
  contentType: string;
}

/**
 * Enumera las fotos que el usuario ha seleccionado, SIN subir nada.
 *
 * ─── Por qué existe ────────────────────────────────────────────────────────
 *
 * Los `saveData` calculan qué imágenes borrar de la BD así:
 *
 *     removeImages = orderImages.filter(o => !selectedImages.some(s => s.name === o.name))
 *
 * es decir, «borra toda imagen existente que no esté en la lista de subidas».
 * Eso funcionaba cuando las fotos se subían de forma bloqueante dentro del
 * guardado. Con el outbox, al guardar esa lista está VACÍA porque las fotos se
 * han encolado — y comparar contra ella marcaría para borrar TODAS las fotos
 * previas de la actuación.
 *
 * La comparación tiene que hacerse contra la INTENCIÓN del usuario, no contra
 * el resultado de la transferencia. Y la intención se conoce sin red: son los
 * ficheros que hay en el directorio OPFS del formulario.
 */
export interface PlanUploadsOptions {
  /**
   * Nombres de fichero a incluir. Si se omite, entra todo el directorio.
   *
   * 🔴 Los flujos de EDICIÓN tienen que pasarlo. El formulario carga en OPFS
   * TODAS las fotos que enseña, incluidas las que ya están en la base de datos,
   * así que enumerar el directorio entero volvía a subirlas y creaba un
   * registro `Image` nuevo por cada una: **duplicados** (visto en vivo el
   * 2026-08-10 al añadir 1 foto a una actuación que ya tenía 30 y ver
   * «Subiendo fotos · 0 de 31»).
   *
   * La lista correcta es la del change-tracking del formulario,
   * `editionData[DataState.added]`, que son solo las nuevas.
   */
  onlyNames?: string[];
  /**
   * Desplaza la numeración: la primera foto será `fromSequence + 1`.
   *
   * 🔴 Lo necesita todo flujo que pueda encolar VARIAS tandas contra la misma
   * orden sin que la anterior haya drenado. El dedupe de la cola borra las
   * entradas `pending` con la misma `(orderId, kind, photoSequence)`, así que
   * una segunda tanda numerada desde 1 se lleva por delante la primera —
   * silenciosamente, y con sus blobs. Es el caso de la cadena de fotos del
   * TRAMO, que se abre al cerrarlo y otra vez desde su card.
   *
   * Por defecto 0: el asistente de actuación no cambia.
   */
  fromSequence?: number;
  /**
   * De dónde leer las fotos. Por defecto, la carpeta OPFS de la pantalla; el
   * reintento de la cola pasa su fuente en memoria (spec outbox-autocontenido v2).
   */
  images?: Pick<FormImageSource, 'list'>;
}

export async function planUploads(
  picturesDirectoryName: string,
  orderMetadata: OrderMetadata,
  { onlyNames, fromSequence = 0, images }: PlanUploadsOptions = {},
): Promise<PlannedUpload[]> {
  // `getFilesFromDirectory` abre el directorio con `create: false`, así que
  // LANZA `NotFoundError` si no existe — y no existe hasta que el asistente
  // guarda la primera foto. «Sin directorio» es «sin fotos», no un error:
  // dejarlo propagar rompería el guardado de toda actuación sin fotos, y
  // encima ANTES del SAVE_ACTION (cazado por el E2E del outbox).
  let entries: Awaited<ReturnType<typeof getFilesFromDirectory>>;
  try {
    entries = images
      ? await images.list(picturesDirectoryName)
      : await getFilesFromDirectory(picturesDirectoryName);
  } catch (err) {
    if ((err as { name?: string })?.name === 'NotFoundError') return [];
    throw err;
  }

  const seleccionadas =
    onlyNames === undefined
      ? entries
      : entries.filter((e) => onlyNames.includes(e.file.name));

  return seleccionadas.map((entry, index) => ({
    name: generateImageNameWithROID({
      ...orderMetadata,
      imageType: 'photo',
      sequence: fromSequence + index + 1,
    }),
    blobFileName: entry.file.name,
    sequence: fromSequence + index + 1,
    contentType: entry.file.type || 'image/jpeg',
  }));
}
